import {
    type FC,
    type ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { IconMinus, IconPlus, IconThreePeople } from 'assets/svg/sprite'
import { IconButton } from 'components/IconButton/IconButton'
import { addToast } from 'components/Toast/Toast'
import { useModalContext } from 'context/modalContext'
import {
    createPromoCampaign,
    updatePromoCampaign
} from 'services/endpoints/settings/general/subscription'
import { type PromoCampaign } from 'types/api/subscription'
import { PromoCampaignType } from 'types/enums'

import style from './_add-promo-campaign.module.scss'

type OpenDropdown =
    | 'audience'
    | 'duration'
    | 'campaignLength'
    | 'offerLimit'
    | null

interface DropdownOption<T extends string | number> {
    value: T
    label: string
}

interface CampaignDropdownProps<T extends string | number> {
    name: Exclude<OpenDropdown, null>
    label?: string
    value: T
    options: Array<DropdownOption<T>>
    openDropdown: OpenDropdown
    setOpenDropdown: React.Dispatch<React.SetStateAction<OpenDropdown>>
    onChange: (value: T) => void
    icon?: ReactNode
    disabled?: boolean
    customClass?: string
    direction?: 'up' | 'down'
}

const CampaignDropdown = <T extends string | number>({
    name,
    label,
    value,
    options,
    openDropdown,
    setOpenDropdown,
    onChange,
    icon,
    disabled = false,
    customClass = '',
    direction = 'down'
}: CampaignDropdownProps<T>) => {
    const isOpen = openDropdown === name
    const optionsRef = useRef<HTMLDivElement>(null)
    const selectedRef = useRef<HTMLButtonElement>(null)
    const selectedOption =
        options.find((option) => option.value === value) ?? options[0]

    const handleToggle = () => {
        if (disabled) return

        setOpenDropdown((current) => (current === name ? null : name))
    }

    const handleSelect = (optionValue: T) => {
        onChange(optionValue)
        setOpenDropdown(null)
    }

    useEffect(() => {
        if (!isOpen) return

        requestAnimationFrame(() => {
            const menu = optionsRef.current
            const selected = selectedRef.current

            if (!menu || !selected) return

            menu.scrollTop =
                selected.offsetTop -
                menu.clientHeight / 2 +
                selected.clientHeight / 2
        })
    }, [isOpen, value])

    return (
        <div
            className={`${style.field_dropdown} ${
                isOpen ? style.field_dropdown_open : ''
            } ${
                isOpen && direction === 'up'
                    ? style.field_dropdown_open_up
                    : ''
            } ${
                disabled ? style.field_dropdown_disabled : ''
            } ${customClass}`}
        >
            <button
                type='button'
                className={style.field_dropdown_selected}
                onClick={handleToggle}
                disabled={disabled}
                aria-expanded={isOpen}
                aria-haspopup='listbox'
            >
                {icon && (
                    <span className={style.field_dropdown_icon}>{icon}</span>
                )}

                <span className={style.field_dropdown_content}>
                    {label && (
                        <span className={style.field_dropdown_label}>
                            {label}
                        </span>
                    )}

                    <span className={style.field_dropdown_value}>
                        {selectedOption.label}
                    </span>
                </span>

                <span
                    className={`${style.field_dropdown_arrow} ${
                        isOpen ? style.field_dropdown_arrow_open : ''
                    }`}
                />
            </button>

            <div
                ref={optionsRef}
                className={`${style.field_dropdown_options} ${
                    direction === 'up'
                        ? style.field_dropdown_options_up
                        : style.field_dropdown_options_down
                } ${
                    isOpen ? style.field_dropdown_options_open : ''
                }`}
                role='listbox'
            >
                {options.map((option) => {
                    const isSelected = option.value === value

                    return (
                        <button
                            ref={isSelected ? selectedRef : null}
                            type='button'
                            role='option'
                            aria-selected={isSelected}
                            className={`${style.field_dropdown_option} ${
                                isSelected
                                    ? style.field_dropdown_option_selected
                                    : ''
                            }`}
                            onClick={() => {
                                handleSelect(option.value)
                            }}
                            key={`${name}-${option.value}`}
                        >
                            {option.label}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

const AddPromoCampaign: FC<{
    campaign?: any
    isEdit?: boolean
}> = ({ campaign, isEdit }) => {
    const { t } = useTranslation()
    const modalData = useModalContext()
    const queryClient = useQueryClient()
    const containerRef = useRef<HTMLDivElement>(null)

    const [openDropdown, setOpenDropdown] = useState<OpenDropdown>(null)

    const [type, setType] = useState<'trial' | 'promo'>(
        campaign?.free === false ? 'promo' : 'trial'
    )

    const [activeCategory, setActiveCategory] = useState<string>(
        campaign?.audience ?? 'new'
    )

    const [duration, setDuration] = useState<number>(
        campaign?.duration ?? 7
    )

    const [length, setLength] = useState<number>(
        campaign?.length ?? 7
    )

    // 0 means unlimited.
    const [limit, setLimit] = useState<number>(
        campaign?.limit ?? 0
    )

    const [discount, setDiscount] = useState<number>(
        campaign?.discount ?? 5
    )

    const audienceOptions: Array<DropdownOption<string>> = [
        {
            value: 'new',
            label: 'New Subscribers'
        },
        {
            value: 'returning',
            label: 'Returning Subscribers'
        },
        {
            value: 'all',
            label: 'New & Returning'
        }
    ]

    const dayOptions = useMemo<Array<DropdownOption<number>>>(
        () =>
            Array.from({ length: 30 }, (_, index) => {
                const day = index + 1

                return {
                    value: day,
                    label: `${day} ${day === 1 ? 'Day' : 'Days'}`
                }
            }),
        []
    )

    const offerLimitOptions = useMemo<Array<DropdownOption<number>>>(
        () => [
            {
                value: 0,
                label: 'No Limit'
            },
            ...Array.from({ length: 10 }, (_, index) => {
                const value = index + 1

                return {
                    value,
                    label: value === 1 ? '1 Subscriber' : `${value} Subscribers`
                }
            }),
            ...Array.from({ length: 9 }, (_, index) => {
                const value = (index + 2) * 10 // 20, 30, ..., 100

                return {
                    value,
                    label: `${value} Subscribers`
                }
            })
        ],
        []
    )
    const reversedDayOptions = useMemo(
        () => [...dayOptions].reverse(),
        [dayOptions]
    )

    const reversedOfferLimitOptions = useMemo(
        () => [...offerLimitOptions].reverse(),
        [offerLimitOptions]
    )
    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setOpenDropdown(null)
            }
        }

        document.addEventListener('mousedown', handleOutsideClick)

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick)
        }
    }, [])

    useEffect(() => {
        if (type === 'trial' && !isEdit) {
            setActiveCategory('new')
        }

        setOpenDropdown(null)
    }, [type, isEdit])

    const addMutation = useMutation(
        async (data: PromoCampaign) => await createPromoCampaign(data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('freeTrials')
                queryClient.invalidateQueries('promoCampaigns')
                toast.dismiss()
                modalData.clearModal()
            },
            onError: (error: any) => {
                toast.dismiss()

                addToast(
                    'error',
                    error?.error?.message ||
                        'An error occurred, please try again'
                )

                modalData.clearModal()
            }
        }
    )

    const editMutation = useMutation(
        async (data: PromoCampaign) =>
            await updatePromoCampaign(campaign.id, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('freeTrials')
                queryClient.invalidateQueries('promoCampaigns')
                toast.dismiss()
                modalData.clearModal()
            },
            onError: (error: any) => {
                toast.dismiss()

                addToast(
                    'error',
                    error?.error?.message ||
                        'An error occurred, please try again'
                )

                modalData.clearModal()
            }
        }
    )

    const isLoading =
        addMutation.isLoading || editMutation.isLoading

    const isValid = () => {
        const validAudience = ['new', 'returning', 'all'].includes(
            activeCategory
        )

        const validCampaignLength =
            Number.isInteger(length) &&
            length >= 1 &&
            length <= 30

        const validOfferLimit =
            limit === 0 ||
            (
                Number.isInteger(limit) &&
                (
                    (limit >= 1 && limit <= 10) ||
                    (limit >= 20 && limit <= 100 && limit % 10 === 0)
                )
            )
        if (type === 'trial') {
            const validDuration =
                Number.isInteger(duration) &&
                duration >= 1 &&
                duration <= 30

            return (
                validAudience &&
                validDuration &&
                validCampaignLength &&
                validOfferLimit
            )
        }

        if (type === 'promo') {
            return (
                validAudience &&
                validCampaignLength &&
                validOfferLimit &&
                discount >= 5 &&
                discount <= 50
            )
        }

        return false
    }

    const createCampaignData = (): PromoCampaign => {
        if (type === 'trial') {
            return {
                type: PromoCampaignType.PROMOTIONAL_CAMPAIGN,
                length: Number(length),
                limit: Number(limit),
                audience: activeCategory,
                duration: Number(duration),
                free: true
            }
        }

        return {
            audience: activeCategory,
            type: PromoCampaignType.PROMOTIONAL_CAMPAIGN,
            length: Number(length),
            limit: Number(limit),
            discount,
            active: true,
            free: false
        }
    }

    const addFunction = () => {
        if (!isValid()) {
            addToast('error', 'Please fill all fields.')
            return
        }

        addToast('loading', 'Creating...')
        addMutation.mutate(createCampaignData())
    }

    const editFunction = () => {
        if (!isValid()) {
            addToast('error', 'Please fill all fields.')
            return
        }

        addToast('loading', 'Updating...')
        editMutation.mutate(createCampaignData())
    }

    return (
        <div
            ref={containerRef}
            className={style.container}
        >
            <CampaignDropdown
                name='audience'
                value={activeCategory}
                options={audienceOptions}
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
                onChange={setActiveCategory}
                icon={<IconThreePeople />}
            />

            <div
                className={`${style.type_buttons_container} ${
                    isEdit
                        ? style.type_buttons_container_disabled
                        : ''
                }`}
            >
                <button
                    type='button'
                    className={`${style.type_button} ${
                        type === 'trial'
                            ? style.type_button_selected
                            : ''
                    }`}
                    onClick={() => {
                        setType('trial')
                    }}
                    disabled={isEdit}
                >
                    Free Trial
                </button>

                <button
                    type='button'
                    className={`${style.type_button} ${
                        type === 'promo'
                            ? style.type_button_selected
                            : ''
                    }`}
                    onClick={() => {
                        setType('promo')
                    }}
                    disabled={isEdit}
                >
                    First Month Discount
                </button>
            </div>

            {type === 'trial' && (
                <>
                    <CampaignDropdown
                        name='duration'
                        label='Free Trial Duration'
                        value={duration}
                        options={dayOptions}
                        openDropdown={openDropdown}
                        setOpenDropdown={setOpenDropdown}
                        onChange={setDuration}
                        customClass={style.single_dropdown}
                        direction='down'
                    />

                    <div className={style.two_inputs_container}>
                        <CampaignDropdown
                            name='campaignLength'
                            label='Campaign Length'
                            value={length}
                            options={reversedDayOptions}
                            openDropdown={openDropdown}
                            setOpenDropdown={setOpenDropdown}
                            onChange={setLength}
                            customClass={
                                style.two_inputs_input_margin
                            }
                            direction='up'
                        />

                        <CampaignDropdown
                            name='offerLimit'
                            label='Offer Limit'
                            value={limit}
                            options={reversedOfferLimitOptions}
                            openDropdown={openDropdown}
                            setOpenDropdown={setOpenDropdown}
                            onChange={setLimit}
                            direction='up'
                        />
                    </div>
                </>
            )}

            {type === 'promo' && (
                <>
                    <div className={style.discount_container}>
                        <p>{t('discount')}</p>

                        <div className={style.discount_price}>
                            <IconButton
                                type='white'
                                icon={<IconMinus />}
                                disabled={discount === 5}
                                clickFn={() => {
                                    setDiscount((current) =>
                                        Math.max(5, current - 5)
                                    )
                                }}
                            />

                            <p>{discount}%</p>

                            <IconButton
                                type='white'
                                icon={<IconPlus />}
                                disabled={discount === 50}
                                clickFn={() => {
                                    setDiscount((current) =>
                                        Math.min(50, current + 5)
                                    )
                                }}
                            />
                        </div>
                    </div>

                    <div className={style.two_inputs_container}>
                        <CampaignDropdown
                            name='campaignLength'
                            label='Campaign Length'
                            value={length}
                            options={reversedDayOptions}
                            openDropdown={openDropdown}
                            setOpenDropdown={setOpenDropdown}
                            onChange={setLength}
                            customClass={
                                style.two_inputs_input_margin
                            }
                            direction='up'
                        />

                        <CampaignDropdown
                            name='offerLimit'
                            label='Offer Limit'
                            value={limit}
                            options={reversedOfferLimitOptions}
                            openDropdown={openDropdown}
                            setOpenDropdown={setOpenDropdown}
                            onChange={setLimit}
                            direction='up'
                        />
                    </div>
                </>
            )}

            <div className={style.submit_buttons_container}>
                <button
                    type='button'
                    className={style.cancel_button}
                    onClick={() => {
                        modalData.clearModal()
                    }}
                    disabled={isLoading}
                >
                    Cancel
                </button>

                <button
                    type='button'
                    className={`${style.submit_button} ${
                        isLoading
                            ? style.submit_button_loading
                            : ''
                    }`}
                    onClick={
                        !isEdit
                            ? addFunction
                            : editFunction
                    }
                    disabled={isLoading}
                >
                    {isEdit ? 'Update' : 'Create'}
                </button>
            </div>
        </div>
    )
}

export default AddPromoCampaign